import { createContext, useContext } from 'react'
import type { MyOrg } from '@carouselgg/auth/use-my-orgs'
import type { TrustScore } from '@carouselgg/auth/types'
import type { MyInvite } from '../../../api/invites'
import type { MyMatch } from '../../../api/matches'
import type { OrgTeamWithApps } from '../../../api/recruit'
import type { MyTeam, OrgSeason, StandingRow } from '../../../api/teams'
import type { CompetitionGroup } from '../competitions'
import type { NeedsYouItem } from '../needsYou'
import type { UpcomingEntry } from '../upcoming'
import type { WidgetContext } from './types'

/**
 * Everything the board fetched, in one place.
 *
 * Widgets read from here rather than fetching their own. `/matches/mine` has
 * three consumers (the schedule, each team's next fixture, the check-in
 * warnings) and `/teams/mine` has four — letting each widget ask for what it
 * needs would turn five requests into a dozen and give every widget its own
 * loading race. It is also the seam that makes a widget testable: a test wraps
 * a fixture in the provider instead of stubbing fetch.
 */
export interface DashboardData {
  invites: MyInvite[]
  matches: MyMatch[]
  teams: MyTeam[]
  seasons: OrgSeason[]
  standings: Record<string, StandingRow[]>
  orgs: MyOrg[]
  trust: TrustScore | null
  /** Teams with an open roster, per org the viewer administers. */
  openRosters: OrgTeamWithApps[]
  /** Applications waiting on a decision, per club the viewer administers. */
  waitingByOrg: Record<string, number>

  // Derived once for everyone, rather than per widget.
  needsYou: NeedsYouItem[]
  upcoming: UpcomingEntry[]
  competitionGroups: CompetitionGroup[]
  /** Re-read the competitions, e.g. after a league contact changed. */
  reloadCompetitions: () => void

  /**
   * Who the viewer is, as the widgets reason about it.
   *
   * Lives here rather than being threaded down as a prop so a widget can stay
   * prop-free — which is what lets the board place one anywhere without knowing
   * what it needs. The board reads the same object to decide which widgets to
   * place at all.
   */
  ctx: WidgetContext

  /**
   * One clock for the whole board. Re-deriving it per widget would let two
   * widgets disagree about what day it is across a midnight render.
   */
  now: Date
  /** Every source has settled. Drives `data-ready`. */
  ready: boolean
}

export const DashboardDataContext = createContext<DashboardData | null>(null)

export function useDashboardData(): DashboardData {
  const value = useContext(DashboardDataContext)
  if (!value) {
    throw new Error('useDashboardData must be used inside a DashboardDataProvider')
  }
  return value
}
